import { ReactNode, useState, useEffect, useMemo, useRef } from 'react';
import { useParams, Link, useSearchParams, useLocation, useNavigate, useNavigationType } from 'react-router-dom';
import { useAuth } from '@clerk/clerk-react';
import { wordpressAPI } from '../services/wordpressApi';
import { recordResourceView } from '../services/recentResourcesApi';
import { hubAPI } from '../services/hubApi';
import { programAPI } from '../services/programApi';
import { WPPost } from '../types/wordpress';
import { Hub } from '../types/hub';
import { Program } from '../types/program';
import { CatalogResource } from '../types/resourceCatalog';
import { catalogLabel } from '../utils/catalogLabels';
import { documentMembership, RowPlace } from '../utils/libraryPlaces';
import PlaceLink from '../components/layout/PlaceLink';
import { OnThisPageItem } from '../types/onThisPage';
import ResourceHeader from '../components/resources/ResourceHeader';
import ResourceContent from '../components/resources/ResourceContent';
import ResourceAttachments from '../components/resources/ResourceAttachments';
import RelatedResources from '../components/resources/RelatedResources';
import Breadcrumb from '../components/layout/Breadcrumb';
import RoomBar from '../components/layout/RoomBar';
import { savedHistoryScroll } from '../components/layout/Layout';
import { PAGE_SIDEBAR_LAYOUT, PageSidebar } from '../components/layout/OnThisPage';
import InThisNav, { InThisNavItem } from '../components/programs/InThisNav';
import LoadingSkeleton from '../components/ui/LoadingSkeleton';
import ErrorState from '../components/ui/ErrorState';
import { addToRecentlyViewed } from '../utils/recentlyViewed';
import { parseHubContext } from '../utils/resourceLinks';
import { decodeHtmlEntities } from '../utils/htmlEntities';
import { flattenTocIds } from '../utils/headingToc';
import { pageFromTo, readPageFrom, reopenSearchState } from '../utils/pageFrom';
import { usePageTitle } from '../hooks/usePageTitle';
import { useScrollSpy } from '../hooks/useScrollSpy';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import { hashScrollBehavior, pinScrollAnchor, withSmoothScroll } from '../utils/hubChapter';
import { subjectPath } from '../utils/placePaths';
import { programHubHref } from '../utils/hubChapter';
import { readHubReturn, returnsToProgram } from '../utils/programScan';
import {
  beginPlaceTransition,
  closePlace,
  isPlainClick,
  peekPlaceRoom,
  peekResourcePost,
  placeEnterClass,
  PlaceCrumb,
  PlaceRoom,
  readPlaceSurface,
  readReplacedDocument,
  rememberPlaceRoom,
  rememberResourcePost,
  roomForHub,
  usePlacePage,
  usePlaceSlide,
} from '../utils/placeMotion';
import ProgramSectionList, {
  visibleProgramSections,
} from '../components/hubs/ProgramSectionList';

const PAGE_SHELL = 'max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8';

function PlaceLine({ label, places }: { label: string; places: RowPlace[] }) {
  if (places.length === 0) return null;
  return (
    <p className="text-sm text-gray-600">
      {label}{' '}
      {places.map((place, index) => (
        <span key={place.href}>
          {index > 0 ? ', ' : null}
          <PlaceLink
            to={place.href}
            surface={{ kind: place.kind, slug: place.slug }}
            className="font-medium text-primary hover:underline"
          >
            {place.title}
          </PlaceLink>
        </span>
      ))}
    </p>
  );
}

function DocumentPlaces({
  membership,
  committed,
}: {
  membership: { hubs: RowPlace[]; programs: RowPlace[] };
  committed: CatalogResource | null;
}) {
  const tags = [
    committed?.contentType ? catalogLabel(committed.contentType) : null,
    ...(committed?.topics ?? []).map((topic) => catalogLabel(topic)),
  ].filter((tag): tag is string => Boolean(tag));
  if (membership.hubs.length === 0 && membership.programs.length === 0 && tags.length === 0) return null;
  return (
    <div className="mb-8 -mt-4 space-y-1">
      <PlaceLine label="In" places={membership.hubs} />
      <PlaceLine label="Part of" places={membership.programs} />
      {tags.length > 0 ? <p className="text-sm text-gray-600">{tags.join(' · ')}</p> : null}
    </div>
  );
}

function sectionNavItems(items: OnThisPageItem[], activeId: string | null): InThisNavItem[] {
  return items.map((item) => ({
    id: item.id,
    label: item.label,
    current: item.id === activeId,
    children: item.children?.length ? sectionNavItems(item.children, activeId) : undefined,
  }));
}

function DocumentSideNav({
  program,
  hubs,
  currentSlug,
  showToc,
  tocItems,
  activeSectionId,
  onSelectSection,
}: {
  program?: { title: string; slug: string } | null;
  hubs?: { title: string; slug: string; resourceCount: number }[];
  currentSlug: string | null;
  showToc: boolean;
  tocItems: OnThisPageItem[];
  activeSectionId: string | null;
  onSelectSection: (id: string) => void;
}) {
  return (
    <>
      {program ? (
        <ProgramSectionList
          program={program}
          hubs={hubs}
          currentSlug={currentSlug ?? undefined}
          className="mb-0"
        />
      ) : null}
      {showToc ? (
        <InThisNav
          label="On this page"
          items={sectionNavItems(tocItems, activeSectionId)}
          onSelect={onSelectSection}
        />
      ) : null}
    </>
  );
}

export default function ResourceDetail() {
  const { slug } = useParams<{ slug: string }>();
  const { getToken, isSignedIn } = useAuth();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const navigationType = useNavigationType();
  const fromHubSlug = parseHubContext(searchParams.toString());
  const panelFrom = readPageFrom(location.state);
  const openedSurface = readPlaceSurface(location.state);
  const slide = usePlaceSlide();
  const pageRef = useRef<HTMLDivElement>(null);
  usePlacePage(pageRef);
  const remembered = roomForHub(peekPlaceRoom(), fromHubSlug);
  const [post, setPost] = useState<WPPost | null>(() => peekResourcePost(slug));
  const [hubContext, setHubContext] = useState<
    Pick<Hub, 'title' | 'program' | 'programHubs'> | null
  >(null);
  const [places, setPlaces] = useState<{ hubs: Hub[]; programs: Program[] }>({ hubs: [], programs: [] });
  const [placesReady, setPlacesReady] = useState(false);
  const [committed, setCommitted] = useState<CatalogResource | null>(null);
  const [hubReady, setHubReady] = useState(!parseHubContext(searchParams.toString()));
  const [tocItems, setTocItems] = useState<OnThisPageItem[]>([]);
  const [loading, setLoading] = useState(() => !peekResourcePost(slug));
  const [error, setError] = useState<Error | null>(null);
  usePageTitle(post ? decodeHtmlEntities(post.title.rendered) : undefined);

  const tocIds = useMemo(() => flattenTocIds(tocItems), [tocItems]);
  const activeSectionId = useScrollSpy(tocIds);

  useEffect(() => {
    const id = decodeURIComponent(location.hash.replace(/^#/, ''));
    if (!id || !post || !document.getElementById(id)) return;
    const saved = savedHistoryScroll(location.key);
    const historyRestore =
      navigationType === 'POP' &&
      ((saved != null && saved > 0) ||
        Boolean(
          location.state &&
            typeof location.state === 'object' &&
            (location.state as { restoreScroll?: boolean }).restoreScroll
        ));
    if (historyRestore) return;
    return pinScrollAnchor(id, hashScrollBehavior(location.state));
  }, [location.hash, location.key, location.state, navigationType, post, tocIds]);

  useEffect(() => {
    setTocItems([]);
  }, [slug]);

  useEffect(() => {
    let cancelled = false;
    const loadPost = async () => {
      if (!slug) {
        setError(new Error('Invalid resource slug'));
        setLoading(false);
        return;
      }

      const cached = peekResourcePost(slug);
      if (cached) {
        setPost(cached);
        setLoading(false);
        setError(null);
        addToRecentlyViewed(cached);
      } else {
        setPost(null);
        setLoading(true);
      }

      try {
        const postData = await wordpressAPI.fetchPostBySlug(slug);
        if (cancelled) return;
        if (!postData) {
          if (!cached) setError(new Error('Resource not found'));
        } else {
          setPost(postData);
          rememberResourcePost(postData);
          addToRecentlyViewed(postData);
        }
      } catch (err) {
        if (!cancelled && !cached) {
          setError(err instanceof Error ? err : new Error('Failed to load resource'));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadPost();
    return () => {
      cancelled = true;
    };
  }, [slug]);

  useEffect(() => {
    if (!post || !isSignedIn) return;
    recordResourceView(getToken, post.id).catch(() => undefined);
  }, [post, isSignedIn, getToken]);

  useEffect(() => {
    let cancelled = false;
    Promise.all([hubAPI.fetchHubs(), programAPI.fetchPrograms()])
      .then(([hubs, programs]) => {
        if (!cancelled) setPlaces({ hubs, programs });
      })
      .catch(() => {
        if (!cancelled) setPlaces({ hubs: [], programs: [] });
      })
      .finally(() => {
        if (!cancelled) setPlacesReady(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!post) return;
    let cancelled = false;
    setCommitted(null);
    const params = new URLSearchParams({ wpIds: String(post.id), perPage: '1' });
    fetch(`/api/tools/resources?${params.toString()}`)
      .then(async (response) => {
        if (!response.ok) return null;
        const body = (await response.json()) as { success?: boolean; data?: CatalogResource[] };
        return body.success ? body.data?.[0] ?? null : null;
      })
      .then((item) => {
        if (!cancelled) setCommitted(item);
      })
      .catch(() => {
        if (!cancelled) setCommitted(null);
      });
    return () => {
      cancelled = true;
    };
  }, [post]);

  useEffect(() => {
    if (!post) return;
    document.getElementById('resource-title')?.focus({ preventScroll: true });
  }, [post]);

  useEffect(() => {
    if (!fromHubSlug) {
      setHubContext(null);
      setHubReady(true);
      return;
    }

    let cancelled = false;
    setHubReady(false);
    hubAPI
      .fetchHubBySlug(fromHubSlug)
      .then((hub) => {
        if (!cancelled) {
          setHubContext(
            hub
              ? {
                  title: hub.title,
                  program: hub.program ?? null,
                  programHubs: hub.programHubs,
                }
              : null
          );
        }
      })
      .catch(() => {
        if (!cancelled) setHubContext(null);
      })
      .finally(() => {
        if (!cancelled) setHubReady(true);
      });

    return () => {
      cancelled = true;
    };
  }, [fromHubSlug]);

  const replaced = readReplacedDocument(location.state);

  const goReplaced = () => {
    if (!replaced) return;
    if (replaced.room) rememberPlaceRoom(replaced.room);
    beginPlaceTransition('replace', () => {
      navigate(replaced.to, { state: replaced.state });
    });
  };

  const goShallower = (to: string, restore: boolean, restoreProgramScan = false) => {
    const current =
      location.state && typeof location.state === 'object'
        ? { ...(location.state as Record<string, unknown>) }
        : {};
    if (restore) current.restoreScroll = true;
    else delete current.restoreScroll;
    if (restoreProgramScan) current.restoreProgramScan = true;
    else delete current.restoreProgramScan;
    beginPlaceTransition('shallower', () => {
      navigate(to, { state: current });
    });
  };

  const frame = (
    room: PlaceRoom | null,
    crumbs: PlaceCrumb[],
    backTo: string,
    backLabel: string,
    restoreBack: boolean,
    body: ReactNode
  ) => {
    const back = (
      <Link
        to={backTo}
        state={restoreBack ? { restoreScroll: true } : undefined}
        onClick={(event) => {
          if (!isPlainClick(event)) return;
          if (replaced) {
            event.preventDefault();
            goReplaced();
            return;
          }
          if (fromHubSlug) {
            event.preventDefault();
            goShallower(backTo, false, returnsToProgram(location.state));
            return;
          }
          closePlace(event, backTo, navigate, openedSurface, reopenSearchState(panelFrom));
        }}
        className="inline-flex items-center gap-2 text-primary hover:text-primary-dark mb-6 transition-colors"
      >
        <ArrowLeftIcon className="h-5 w-5" />
        <span>{backLabel}</span>
      </Link>
    );
    const motion = `place-content ${placeEnterClass(slide)}`;
    if (!room) {
      return (
        <div ref={pageRef} data-place-page className={`${PAGE_SHELL} ${motion}`}>
          <Breadcrumb items={crumbs} />
          {back}
          {body}
        </div>
      );
    }
    return (
      <div ref={pageRef} data-place-page className="bg-white">
        <div className="place-room">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8">
            <Breadcrumb items={crumbs} />
          </div>
          <RoomBar
            title={room.title}
            to={room.to}
            onNavigate={(event) => {
              if (!isPlainClick(event)) return;
              event.preventDefault();
              goShallower(room.to, room.to === room.backTo);
            }}
          />
        </div>
        <div className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 ${motion}`}>
          {back}
          {body}
        </div>
      </div>
    );
  };

  const waitingForHub = Boolean(fromHubSlug) && !hubReady && !remembered;
  const openedOnProgram = returnsToProgram(location.state);
  if ((!post && loading) || waitingForHub) {
    return frame(
      remembered,
      remembered?.crumbs ?? [
        { label: 'Home', to: '/', step: 'leave' },
        { label: 'Resources', to: '/resources', step: 'leave' },
      ],
      replaced
        ? replaced.to
        : openedOnProgram && remembered
          ? remembered.to
          : remembered?.backTo ?? '/resources',
      replaced
        ? `Back to ${replaced.title}`
        : openedOnProgram && remembered
          ? `Back to ${remembered.title}`
          : remembered?.backLabel ?? 'Back to Resources',
      Boolean(remembered),
      <>
        <LoadingSkeleton lines={10} className="mb-4" />
        <LoadingSkeleton lines={5} />
      </>
    );
  }

  if (error || !post) {
    return (
      <div className={PAGE_SHELL}>
        <ErrorState
          error={error || new Error('Resource not found')}
          title="Resource Not Found"
          onRetry={() => window.location.reload()}
        />
      </div>
    );
  }

  const membership = placesReady
    ? documentMembership(post.id, places.hubs, places.programs)
    : { hubs: [], programs: [] };
  const placedHub =
    (fromHubSlug ? membership.hubs.find((hub) => hub.slug === fromHubSlug) : undefined) ??
    membership.hubs[0];
  const placedProgramSlug = (() => {
    if (!placedHub) return undefined;
    const parts = placedHub.href.split('/').filter(Boolean);
    if (parts[0] === 'toolkits' || parts[0] === 'resources' || parts[0] === 'references') return undefined;
    return parts[0];
  })();
  const placedProgram =
    (placedProgramSlug
      ? membership.programs.find((program) => program.slug === placedProgramSlug)
      : undefined) ?? membership.programs[0];
  const hasHubContext = Boolean(fromHubSlug && hubContext);
  const pendingRoom = !hubReady ? remembered : null;
  const hubHref = hubContext?.program && fromHubSlug
    ? programHubHref(hubContext.program.slug, fromHubSlug)
    : hasHubContext
      ? `/toolkits/${fromHubSlug}`
      : pendingRoom?.backTo ?? '/resources';
  const hubReturn = readHubReturn(location.state);
  const backTo = replaced
    ? replaced.to
    : openedOnProgram && hubContext?.program
    ? subjectPath(hubContext.program.slug)
    : hubReturn
      ? hubReturn
      : fromHubSlug
      ? hubHref
      : panelFrom
        ? pageFromTo(panelFrom)
        : placedHub
          ? placedHub.href
          : placedProgram
            ? placedProgram.href
            : '/resources';
  const backLabel = replaced
    ? `Back to ${replaced.title}`
    : openedOnProgram && hubContext?.program
    ? `Back to ${hubContext.program.title}`
    : fromHubSlug
      ? hasHubContext
        ? `Back to ${hubContext!.title}`
        : pendingRoom?.backLabel ?? 'Back'
      : panelFrom
        ? panelFrom.label
        : placedHub
          ? `Back to ${placedHub.title}`
          : placedProgram
            ? `Back to ${placedProgram.title}`
            : 'Back to Resources';
  const showToc = tocItems.length > 0;
  const showSections = visibleProgramSections(hubContext?.programHubs).length > 0;
  const showSidebar = showToc || showSections;
  const selectSection = (id: string) => {
    if (location.hash === `#${id}`) {
      pinScrollAnchor(id, 'smooth');
      return;
    }
    navigate({ hash: id }, { preventScrollReset: true, state: withSmoothScroll(location.state) });
  };
  const room: PlaceRoom | null = hasHubContext
    ? {
        title: hubContext!.program?.title ?? hubContext!.title,
        to: hubContext!.program ? subjectPath(hubContext!.program.slug) : hubHref,
        backTo: hubHref,
        backLabel: `Back to ${hubContext!.title}`,
        crumbs: [],
      }
    : pendingRoom;
  const titleCrumb = decodeHtmlEntities(post.title.rendered);
  const placedCrumbs: PlaceCrumb[] | null =
    placesReady && (placedProgram || placedHub)
      ? [
          { label: 'Home', to: '/', step: 'leave' },
          ...(placedProgram
            ? [{ label: placedProgram.title, to: placedProgram.href, step: 'shallower' as const }]
            : []),
          ...(placedHub
            ? [{ label: placedHub.title, to: placedHub.href, step: 'shallower' as const }]
            : []),
          { label: titleCrumb },
        ]
      : null;
  const hubBreadcrumb: PlaceCrumb[] = placedCrumbs
    ? placedCrumbs
    : hasHubContext
    ? [
        { label: 'Home', to: '/', step: 'leave' },
        ...(hubContext?.program
          ? [{ label: hubContext.program.title, to: subjectPath(hubContext.program.slug), step: 'shallower' as const }]
          : []),
        { label: hubContext!.title, to: hubHref, step: 'shallower' as const },
        { label: titleCrumb },
      ]
    : pendingRoom?.crumbs?.length
      ? pendingRoom.crumbs
      : [
          { label: 'Home', to: '/', step: 'leave' },
          { label: 'Resources', to: '/resources', step: 'leave' },
          { label: titleCrumb },
        ];

  return frame(
    room,
    hubBreadcrumb,
    backTo,
    backLabel,
    Boolean(hasHubContext || panelFrom || pendingRoom),
    <div className={showSidebar ? PAGE_SIDEBAR_LAYOUT : undefined}>
      {showSidebar ? (
        <div className="max-lg:hidden lg:contents">
          <PageSidebar>
            <DocumentSideNav
              program={hubContext?.program}
              hubs={hubContext?.programHubs}
              currentSlug={fromHubSlug}
              showToc={showToc}
              tocItems={tocItems}
              activeSectionId={activeSectionId}
              onSelectSection={selectSection}
            />
          </PageSidebar>
        </div>
      ) : null}
      <article className="max-w-3xl min-w-0">
        <header>
          <ResourceHeader post={post} />
          <DocumentPlaces
            membership={documentMembership(post.id, places.hubs, places.programs)}
            committed={committed}
          />
        </header>
        {showSidebar ? (
          <div className="lg:hidden">
            <DocumentSideNav
              program={hubContext?.program}
              hubs={hubContext?.programHubs}
              currentSlug={fromHubSlug}
              showToc={showToc}
              tocItems={tocItems}
              activeSectionId={activeSectionId}
              onSelectSection={selectSection}
            />
          </div>
        ) : null}
        <ResourceContent content={post.content.rendered} onHeadings={setTocItems} />
        <ResourceAttachments postId={post.id} contentHtml={post.content.rendered} />
        <RelatedResources
          post={post}
          hubs={places.hubs}
          placesReady={placesReady}
          currentHubSlug={fromHubSlug}
        />
      </article>
    </div>
  );
}
